"use client";

import { useState } from 'react';
import { createUnhandledRejection, logClientMessage, throwClientError, throwAndCatchErrorSync } from '../lib/playground';
import { crashingAction } from './actions'
import Link from 'next/link'

function Spinner() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-opacity-0">
      <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-blue-500"></div>
    </div>
  );
}

function Supported() {
  return (<span className="ml-2 text-green-500">&#10003;</span>);
}

function Unsupported() {
  return (<span className="ml-2 text-red-500">&#10007;</span>);
}

function PartiallySupported() {
  return (<span className="ml-2 text-yellow-500 text-xs">&#9888;</span>);
}

export default function Home() {
  const [isLoading, setIsLoading] = useState(false);

  async function callPlayground(action: string) {
    setIsLoading(true);
    try {
      await fetch('/api/playground', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
    } catch (error) {
      console.error('Error calling playground API:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function crashBrowser() {
    console.info('About to crash the browser! This will not be collected from local server.');
    let txt = "a";
    while(1){
        txt = txt += "a";
    }
  }

  return (
    <div className="p-8 bg-white/90 w-130 mt-10 z-10 text-black mx-auto border-2 border-gray-800">
      {isLoading && <Spinner />}
      <h1 className="mb-6 text-3xl font-extrabold">Telemetry Playground: Next.js</h1>

      <h2 className="mb-4 text-2xl font-extrabold">Server</h2>
      <ul className="m-1 max-w-md space-y-1 text-gray-600 list-disc list-inside">
        <li>
          <button type="button" className="underline cursor-pointer" 
            onClick={() => callPlayground('throwErrorWithSdkSync')}>
            Throw and catch server error - force flush
          </button>
          <Supported />
        </li>
        <li>
          <button type="button" className="underline cursor-pointer" 
            onClick={() => callPlayground('throwErrorWithSdkAsync')}>
            Throw and catch server error - async
          </button>
          <Supported />
        </li>
        <li>
          <Link type="button" target="_blank" rel="noopener noreferrer" className="underline cursor-pointer" 
            href="/crash?crash=1">
            Throw uncaught server error - new page render
          </Link>
          <Supported />
        </li>
        <li>
          <Link type="button" target="_blank" rel="noopener noreferrer" className="underline cursor-pointer" 
            href="/crash/middleware?crash=1">
            Throw uncaught server error - middleware
          </Link>
          <Supported />
        </li>
        <li>
          <button type="button" className="underline cursor-pointer" 
            onClick={() => callPlayground('throwUncaughtError')}>
            Throw uncaught server error - route
          </button>
          <Supported />
        </li>
        <li>
          <button type="button" className="underline cursor-pointer" 
            onClick={crashingAction}>
            Throw uncaught server error - server action
          </button>
          <Supported />
        </li>
      </ul>

      <h2 className="mt-4 mb-4 text-2xl font-extrabold">Browser</h2>
      <ul className="m-1 max-w-md space-y-1 text-gray-600 list-disc list-inside">
        <li>
          <button type="button" className="underline cursor-pointer" 
            onClick={() => throwAndCatchErrorSync('browser')}>
            Throw and catch browser error
          </button>
          <Supported />
        </li>
        <li>
          <button type="button" className="underline cursor-pointer"
            onClick={() => throwClientError()}>
            Throw uncaught browser error
          </button>
          <Supported />
        </li>
        <li>
          <Link type="button" target="_blank" rel="noopener noreferrer" className="underline cursor-pointer" 
            href="/crashclient">
            Throw uncaught browser error - with fallback error UI
          </Link>
          <Supported />
        </li>
        <li>
          <button type="button" className="underline cursor-pointer"
            onClick={() => createUnhandledRejection()}>
            Throw unhandled rejection in browser
          </button>
          <Supported />
        </li>
      </ul>

      <h2 className="mb-4 mt-6 text-2xl font-extrabold">Without SDK</h2>
      <ul className="m-1 max-w-md space-y-1 text-gray-600 list-disc list-inside">                
        <li>
          <button type="button" className="underline cursor-pointer" 
            onClick={() => callPlayground('logMessage')}>
            Log message on server
          </button>
          <Supported />
        </li>
        <li>
          <button type="button" className="underline cursor-pointer" 
            onClick={() => logClientMessage()}>
            Log message on client
          </button>
          <Unsupported />
        </li>  
        <li>
          <button type="button" className="underline cursor-pointer" 
            onClick={() => crashBrowser()}>
            Crash the browser
          </button>
          <PartiallySupported />
        </li>              
      </ul>
    </div>
  );
}
